import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Copy,
  RefreshCw,
  Info,
  BookOpen,
  ShieldCheck,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { validateIsbn, isbnKindDescription, type IsbnResult } from "@/lib/isbn";

const PATH = "/isbn-barcode-reader";
const TITLE = "ISBN Barcode Reader — Validate ISBN-10 & ISBN-13 Online";
const DESC =
  "Free ISBN barcode reader and validator. Check ISBN-10 and ISBN-13 check digits, convert between formats, and decode book barcodes — all in your browser.";

const FAQ = [
  {
    q: "What's the Difference Between ISBN-10 and ISBN-13?",
    a: "ISBN-10 was the standard for books published before 2007 and uses a mod-11 check digit where the last character can be 'X' (representing 10). ISBN-13 is the modern format used worldwide. It's 13 digits long, starts with the Bookland prefix 978 or 979, and uses the same mod-10 check digit as EAN-13.",
  },
  {
    q: "How Do I Handle the 'X' at the End of an ISBN-10?",
    a: "The 'X' is a valid check digit, not a typo. It represents the value 10 in the mod-11 algorithm. Paste it exactly as printed — this ISBN-10 validator accepts both uppercase and lowercase 'x'.",
  },
  {
    q: "Can Every ISBN-13 Be Converted Back to ISBN-10?",
    a: "Only ISBN-13s that start with 978 can be converted back to a legacy ISBN-10. Codes starting with 979 were minted after the ISBN-13 transition and have no ISBN-10 equivalent. The converter shows the ISBN-10 form whenever conversion is possible.",
  },
  {
    q: "Is an ISBN-13 the Same as an EAN-13?",
    a: "Structurally, yes — every ISBN-13 is a valid EAN-13. The Bookland prefix (978 or 979) tells retail systems to treat it as a book identifier. You can also paste an ISBN-13 into the EAN Lookup tool and the check digit will validate.",
  },
  {
    q: "Does This Tool Look up the Book Title?",
    a: "No. This is a pure book barcode scanner and ISBN validator — it confirms the number is structurally correct and converts between formats. Title lookup requires a third-party database (Open Library, Google Books, ISBNdb).",
  },
  {
    q: "Why Does My ISBN Print With Hyphens?",
    a: "Hyphens group the ISBN into prefix, registration group, registrant, publication, and check digit segments — they're cosmetic. This validator strips spaces, dashes, dots and underscores before checking, so paste your ISBN exactly as printed.",
  },
  {
    q: "What Is the Barcode on a Book Called?",
    a: "It is an EAN-13 barcode, often called a Bookland EAN because its number starts with 978 or 979. The bars encode the ISBN-13 of that specific edition. A smaller five-digit add-on printed to its right, when present, carries price information and is not part of the ISBN.",
  },
  {
    q: "Does an ISBN Come With a Barcode?",
    a: "Not automatically. An ISBN agency assigns the number, while the barcode is a separate image generated from the ISBN-13, which some agencies and barcode services charge for. Validate the ISBN-13 here before ordering or generating that image, so the check digit under the bars is right.",
  },
  {
    q: "Where Is the Barcode on a Book?",
    a: "On most printed books it sits on the back cover, usually near the lower right corner, with the ISBN printed in digits next to the bars. Hardcovers with a dust jacket carry it on the back of the jacket. The copyright page inside the book normally lists the ISBN in text as well, which helps when the cover barcode is damaged.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "ISBN Barcode Reader", path: PATH },
];

export const Route = createFileRoute("/isbn-barcode-reader")({
  component: IsbnPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode ISBN Barcode Reader",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function IsbnPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="ISBN Barcode Reader"
      description="Validate ISBN-10 and ISBN-13 book barcodes, convert between formats, and learn how the Bookland prefix works. Runs locally in your browser. If the check digit is wrong, the reader shows the expected digit and offers a corrected ISBN you can validate with one click."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About This ISBN Barcode Reader"
            about={
              <>
                <p>
                  This ISBN barcode reader validates both the legacy ISBN-10 format and the modern
                  13-digit ISBN used on every book printed today. It checks the check digit with the
                  correct algorithm for each format — mod-11 with an 'X' fallback for ISBN-10, and the
                  GS1 mod-10 for ISBN-13 — and converts between the two whenever the prefix allows it.
                </p>
                <p>
                  Everything runs in your browser. No upload, no signup, no third-party lookup of
                  confidential pre-publication catalog data.
                </p>
              </>
            }
            whenToUse={[
              "Validating an ISBN scraped from a publisher's catalog before importing it into your library system.",
              "Cleaning up a list of book identifiers that mixes ISBN-10s and ISBN-13s.",
              "Confirming that a self-published title's freshly assigned ISBN-13 has the right check digit before sending to a printer.",
              "Converting between ISBN-10 and ISBN-13 when migrating legacy bibliographic data.",
              "Debugging why a book listing is being rejected by Amazon, Ingram, or a library catalog feed.",
            ]}
            howItWorks={[
              {
                title: "Paste the ISBN",
                desc: "Type or paste a 10- or 13-character ISBN. Hyphens, spaces, dots and underscores are stripped automatically.",
              },
              {
                title: "Auto-detect the format",
                desc: "Length determines whether it's treated as ISBN-10 (10 chars, last may be X) or ISBN-13 (13 digits, must start 978/979).",
              },
              {
                title: "Run the right check",
                desc: "ISBN-10 uses a mod-11 weighted sum; ISBN-13 uses the standard EAN-13 mod-10. Mismatches show the expected check digit.",
              },
              {
                title: "Convert when possible",
                desc: "Valid ISBN-10s are converted to ISBN-13 (978 prefix). Valid 978-ISBN-13s are converted back to ISBN-10. 979-codes have no ISBN-10 form.",
              },
              {
                title: "Copy the cleaned number",
                desc: "Copy the normalized hyphen-free ISBN — the form retailers and feeds expect.",
              },
            ]}
            whatToAvoid={[
              "Treating a lowercase 'x' in an ISBN-10 as invalid — it's the same as 'X' and represents the value 10.",
              "Assuming every ISBN-13 can be downgraded to ISBN-10. Only the 978-prefix range converts.",
              "Using an ISSN (serial publications) where an ISBN is expected — they look similar but use different ranges and algorithms.",
              "Importing ISBNs straight from a spreadsheet without trimming hidden apostrophes or non-breaking spaces.",
              "Hand-fixing the check digit by guessing instead of recomputing it — typos in the body almost always look like one-off check digit errors.",
            ]}
            tips={[
              "If validation fails by one digit, the typo is almost always in the body of the ISBN, not the check digit.",
              "Every ISBN-13 is also a valid EAN-13 — paste it into the EAN Lookup tool to inspect the GS1 country prefix.",
              "Bookstores often print the ISBN-13 above the bars and the older ISBN-10 below. Both should resolve to the same book.",
              "Need to recompute a missing ISBN-13 check digit from the first 12 characters? Use the Check Digit Calculator.",
              "When in doubt about a barcode's format, the Barcode Type Detector will identify ISBN-10 / ISBN-13 alongside UPC/EAN/GTIN.",
            ]}
          />
          <h2>How to Scan an ISBN Barcode</h2>
          <p>To scan an ISBN barcode, read the EAN-13 symbol on the back cover with the <Link to="/barcode-reader">online barcode reader</Link>, then paste its 13 digits above and press Validate. This page checks the number; it is not an ISBN barcode lookup for titles.</p>
          <h3>ISBN Barcode With Price Add-On</h3>
          <p>The five-digit add-on beside a book ISBN barcode encodes a price and is not part of the ISBN. Under US book industry guidelines, 52495 means $24.95 and 90000 means no price. Paste only the 13 main digits, since this validator rejects 18-digit input.</p>
          <h2>Are ISBN and Barcode the Same?</h2>
          <p>No. The ISBN is the number, and the barcode is the EAN-13 symbol that carries it for scanners.</p>
          <p>The ISBN-13 and the EAN-13 share the mod-10 check digit explained in our <Link to="/barcode-guides/check-digits-explained">guide to how check digits work</Link>. EAN-13 itself is covered in the <Link to="/barcode-guides/barcode-formats">barcode formats field guide</Link>.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <IsbnApp />
      <BookBarcodeExplainer />
    </ToolLayout>
  );
}

type State = IsbnResult | { ok: "idle" };

function IsbnApp() {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const result: State = useMemo(() => {
    if (!submitted) return { ok: "idle" } as const;
    return validateIsbn(value);
  }, [submitted, value]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const reset = () => {
    setValue("");
    setSubmitted(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <label htmlFor="isbn-input" className="text-sm font-medium">
              ISBN
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="isbn-input"
                autoComplete="off"
                placeholder="e.g. 978-0-321-76572-3 or 0-13-110362-8"
                maxLength={24}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSubmitted(false);
                }}
                aria-invalid={"ok" in result && result.ok === false}
              />
              <Button type="submit" disabled={!value.trim()}>
                <ShieldCheck className="mr-2 h-4 w-4" /> Validate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Accepts ISBN-10 (9 digits + digit or X) or ISBN-13 (13 digits, 978/979 prefix).
              Hyphens and spaces are ignored.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setValue(ex);
                    setSubmitted(true);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ResultPanel
        result={result}
        onUseSuggested={(s) => {
          setValue(s);
          setSubmitted(true);
        }}
      />
    </div>
  );
}

const EXAMPLES = ["978-0-321-76572-3", "0-13-110362-8", "043942089X", "9780262033848"];

function ResultPanel({
  result,
  onUseSuggested,
}: {
  result: State;
  onUseSuggested: (s: string) => void;
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1">
          {"ok" in result && result.ok === "idle" && (
            <EmptyState>
              Paste an ISBN and press Validate. The detected format, cleaned number, and the
              matching ISBN-10/ISBN-13 conversion will appear here.
            </EmptyState>
          )}

          {"ok" in result && result.ok === false && (
            <InvalidView result={result} onUseSuggested={onUseSuggested} />
          )}

          {"ok" in result && result.ok === true && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Valid {result.kind}</span>
                <Badge variant="outline" className="text-xs">
                  Check digit {result.checkDigit}
                </Badge>
              </div>
              <Row label="Detected format" value={result.kind} />
              <Row label="Cleaned number" value={result.normalized} mono onCopy={copy} />
              {result.converted && (
                <Row
                  label={`Converted ${result.converted.kind}`}
                  value={result.converted.value}
                  mono
                  onCopy={copy}
                />
              )}
              {!result.converted && result.kind === "ISBN-13" && (
                <p className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                  <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
                  This ISBN-13 starts with 979 and has no ISBN-10 equivalent.
                </p>
              )}
              <p className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
                {isbnKindDescription(result.kind)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InvalidView({
  result,
  onUseSuggested,
}: {
  result: Extract<IsbnResult, { ok: false }>;
  onUseSuggested: (s: string) => void;
}) {
  let suggested: string | null = null;
  if (result.kind && result.expected !== undefined && result.cleaned.length >= 10) {
    suggested = `${result.cleaned.slice(0, -1)}${result.expected}`;
  }
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Invalid ISBN</span>
      </div>
      <p className="text-sm text-muted-foreground">{result.reason}</p>
      {result.cleaned && <Row label="Cleaned input" value={result.cleaned} mono />}
      {suggested && (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-background p-3">
          <p className="text-sm">
            <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
            If the first {suggested.length - 1} characters are correct, the {result.kind} should be{" "}
            <span className="font-mono">{suggested}</span>.
          </p>
          <div>
            <Button size="sm" variant="outline" onClick={() => onUseSuggested(suggested!)}>
              Use suggested check digit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {onCopy && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCopy(value)}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <BookOpen className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function BookBarcodeExplainer() {
  const parts = [
    {
      label: "Prefix",
      value: "978 / 979",
      desc: "Bookland GS1 prefix that flags the code as a book.",
    },
    {
      label: "Registration group",
      value: "1–5 digits",
      desc: "Country, geographic, or language area (e.g. 0 / 1 = English-speaking).",
    },
    {
      label: "Registrant",
      value: "Publisher",
      desc: "Assigned to a specific publisher by their national ISBN agency.",
    },
    {
      label: "Publication",
      value: "Title",
      desc: "Identifies the specific edition and format of the title.",
    },
    {
      label: "Check digit",
      value: "1 char",
      desc: "mod-11 for ISBN-10 ('X' = 10); mod-10 for ISBN-13.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">How a Book Barcode Is Built</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        An ISBN-13 packs five segments into 13 digits. The hyphens you see in print are purely
        visual — the underlying barcode never stores them.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {parts.map((p) => (
          <div key={p.label} className="rounded-lg border border-border bg-background p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{p.label}</div>
            <div className="mt-1 font-mono text-sm">{p.value}</div>
            <p className="mt-1 text-xs text-muted-foreground">{p.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Every ISBN-13 is a valid EAN-13 — inspect the GS1 prefix and structure.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Validate ISBN-13 alongside GTIN-8/12/13/14 with the same mod-10 check.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Recompute the missing ISBN-13 / EAN-13 check digit from the body.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Identify whether a number is an ISBN-10, ISBN-13, UPC or EAN.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan a printed book barcode with your camera and decode it.",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Decode a book barcode from a photo or screenshot.",
    },
    {
      path: "/barcode-guides/check-digits-explained",
      label: "Check Digits Explained",
      desc: "The mod-10 and ISBN-10 mod-11 arithmetic behind every check digit.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "Where EAN-13, UPC-A and other symbologies are used and what they encode.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
